import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../../../utils/config';

interface RFQWorkflowSettingsProps {
    companyId: string;
    token: string;
}

const RFQWorkflowSettings: React.FC<RFQWorkflowSettingsProps> = ({ companyId, token }) => {
    const [requireApproval, setRequireApproval] = useState(true);
    const [requireControl, setRequireControl] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Load workflow settings
    useEffect(() => {
        loadWorkflowSettings();
    }, [companyId]);

    const loadWorkflowSettings = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/rfq-workflow`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setRequireApproval(data.data.require_approval ?? true);
                    setRequireControl(data.data.require_control ?? true);
                }
            }
        } catch (error) {
            console.error('Error loading workflow settings:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const saveWorkflowSettings = async () => {
        setIsSaving(true);
        try {
            const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/rfq-workflow`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    require_approval: requireApproval,
                    require_control: requireControl
                })
            });

            if (response.ok) {
                alert('RFQ workflow settings saved successfully!');
            } else {
                throw new Error('Failed to save settings');
            }
        } catch (error) {
            console.error('Error saving workflow settings:', error);
            alert('Failed to save settings. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <div style={{ padding: '20px' }}>Loading workflow settings...</div>;
    }

    return (
        <div style={{
            marginTop: '30px',
            padding: '24px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
        }}>
            <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '8px',
                color: '#111827'
            }}>
                RFQ Workflow Configuration
            </h3>
            <p style={{
                fontSize: '14px',
                color: '#6b7280',
                marginBottom: '20px'
            }}>
                Configure which approval steps are required for RFQs in this company
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Approve Toggle */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px',
                    backgroundColor: '#ffffff',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                }}>
                    <div>
                        <div style={{
                            fontSize: '15px',
                            fontWeight: '500',
                            color: '#111827',
                            marginBottom: '4px'
                        }}>
                            Require Approval Step
                        </div>
                        <div style={{
                            fontSize: '13px',
                            color: '#6b7280'
                        }}>
                            RFQs must be approved before proceeding to document preparation
                        </div>
                    </div>
                    <label style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '52px',
                        height: '28px',
                        cursor: 'pointer'
                    }}>
                        <input
                            type="checkbox"
                            checked={requireApproval}
                            onChange={(e) => setRequireApproval(e.target.checked)}
                            style={{
                                opacity: 0,
                                width: 0,
                                height: 0
                            }}
                        />
                        <span style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: requireApproval ? '#2563eb' : '#d1d5db',
                            borderRadius: '28px',
                            transition: 'background-color 0.2s'
                        }}>
                            <span style={{
                                position: 'absolute',
                                content: '',
                                height: '20px',
                                width: '20px',
                                left: requireApproval ? '28px' : '4px',
                                bottom: '4px',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                transition: 'left 0.2s'
                            }} />
                        </span>
                    </label>
                </div>

                {/* Control Toggle */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px',
                    backgroundColor: '#ffffff',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                }}>
                    <div>
                        <div style={{
                            fontSize: '15px',
                            fontWeight: '500',
                            color: '#111827',
                            marginBottom: '4px'
                        }}>
                            Require Control Step
                        </div>
                        <div style={{
                            fontSize: '13px',
                            color: '#6b7280'
                        }}>
                            RFQs require control verification after approval
                        </div>
                    </div>
                    <label style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '52px',
                        height: '28px',
                        cursor: 'pointer'
                    }}>
                        <input
                            type="checkbox"
                            checked={requireControl}
                            onChange={(e) => setRequireControl(e.target.checked)}
                            style={{
                                opacity: 0,
                                width: 0,
                                height: 0
                            }}
                        />
                        <span style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: requireControl ? '#2563eb' : '#d1d5db',
                            borderRadius: '28px',
                            transition: 'background-color 0.2s'
                        }}>
                            <span style={{
                                position: 'absolute',
                                content: '',
                                height: '20px',
                                width: '20px',
                                left: requireControl ? '28px' : '4px',
                                bottom: '4px',
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                transition: 'left 0.2s'
                            }} />
                        </span>
                    </label>
                </div>
            </div>

            {/* Save Button */}
            <button
                onClick={saveWorkflowSettings}
                disabled={isSaving}
                style={{
                    marginTop: '20px',
                    padding: '10px 20px',
                    backgroundColor: isSaving ? '#9ca3af' : '#2563eb',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s'
                }}
            >
                {isSaving ? 'Saving...' : 'Save Workflow Settings'}
            </button>
        </div>
    );
};

export default RFQWorkflowSettings;
